import NameAndId from '@src/components/NameAndId.vue'
import UpdateOrCreatePlatformTaxDocumentForm from '@src/partials/platforms/UpdateOrCreatePlatformTaxDocumentForm.vue'
import { PlatformTaxDocument } from '@src/types'
import { uploadPrivate } from '@src/utils/api/s3'
import { useLanguageStore } from '@src/utils/stores/languages'
import { useUserStore } from '@src/utils/stores/user'
import { computed, h, ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ModalButtonStyles, OpenModal, useTable } from '../common'

export const usePlatformTaxDocumentsTable = (
    taxDocuments: Ref<PlatformTaxDocument[]>,
    update: (successMessage?: string | null) => Promise<void>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>
) => {
    const userStore = useUserStore()
    const i18n = useI18n()
    const languageStore = useLanguageStore()

    const {
        TableWrapper: PlatformTaxDocumentsTable,
        selected: selectedPlatformTaxDocumentsIds,
    } = useTable({
        tableProps: {
            id: 'platform_tax_documents',
            label: 'platform_tax_documents',
            help: 'help_platform_tax_documents_table',
            data: taxDocuments,
            total: taxDocuments.value.length,
            columns: [
                {
                    label: 'id',
                    key: 'id',
                    select: true,
                },
                {
                    label: 'id',
                    key: 'id',
                    condition: userStore.can('see_id_columns'),
                    copy: true,
                },
                {
                    label: 'name',
                    key: 'name',
                },
                {
                    label: 'language',
                    key: 'language',
                    cellRenderer: (props: { rowData: PlatformTaxDocument }) => {
                        return (
                            languageStore.languages.find(
                                (language) =>
                                    language.value === props.rowData.language
                            )?.label || props.rowData.language
                        )
                    },
                },
            ],
            actions: [
                {
                    action: handleViewPlatformTaxDocument,
                    icon: 'fa-eye',
                    title: 'view',
                },
                {
                    action: handleEditOrCreatePlatformTaxDocument,
                    icon: 'fa-solid fa-pen',
                    title: 'edit',
                    condition: userStore.can('update_platform'),
                },
                {
                    action: handleDeletePlatformTaxDocument,
                    icon: 'fas fa-trash-alt',
                    title: 'delete',
                    color: 'text-danger-500',
                    condition: userStore.can('update_platform'),
                },
            ],
        },
    })

    function handleViewPlatformTaxDocument(
        platformTaxDocument: PlatformTaxDocument
    ) {
        const url = platformTaxDocument.url
        if (url) {
            window.open(url)
        }
    }

    function handleDeletePlatformTaxDocument(
        platformTaxDocument: PlatformTaxDocument
    ) {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_attachment',
                title: 'modal_title_delete_platform_tax_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deletePlatformTaxDocument(platformTaxDocument),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: [platformTaxDocument],
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deletePlatformTaxDocument(
        platformTaxDocumentToDelete: PlatformTaxDocument,
        submit = true
    ) {
        const taxDocumentsTemp = [...taxDocuments.value]
        taxDocuments.value.splice(
            taxDocuments.value.findIndex(
                (platformTaxDocument) =>
                    platformTaxDocumentToDelete.id == platformTaxDocument.id
            ),
            1
        )
        if (submit) {
            update(
                i18n.t('toast_success_platform_tax_document_deleted', {
                    platformTaxDocument: platformTaxDocumentToDelete.name,
                })
            )
                .catch((error) => {
                    taxDocuments.value = taxDocumentsTemp
                    throw error
                })
                .then(() => {
                    selectedPlatformTaxDocumentsIds.value = []
                    isModalOpen.value = false
                })
        }
    }

    function handleEditOrCreatePlatformTaxDocument(
        platformTaxDocument: PlatformTaxDocument = new PlatformTaxDocument()
    ) {
        const editedPlatformTaxDocument = ref(new PlatformTaxDocument())
        editedPlatformTaxDocument.value = { ...platformTaxDocument }

        const file = ref<File | null>(null)
        isModalOpen.value = true

        const disableCreate = computed(() => {
            return !(
                editedPlatformTaxDocument.value.upload_key &&
                editedPlatformTaxDocument.value.language
            )
        })

        openModal(
            {
                id: 'edit_platform_tax_document',
                title: editedPlatformTaxDocument.value.id
                    ? 'modal_title_update_platform_tax_document'
                    : 'modal_title_create_platform_tax_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    savePlatformTaxDocument(
                        editedPlatformTaxDocument.value,
                        file.value as File
                    ),
            },
            () =>
                h(UpdateOrCreatePlatformTaxDocumentForm, {
                    // eslint-disable-next-line quote-props
                    modelValue: editedPlatformTaxDocument,
                    'onUpdate:modelValue': (
                        value: unknown,
                        files: Ref<FileList>
                    ) => {
                        file.value = files.value?.item(0)
                    },
                }),
            {
                submitButtonText: editedPlatformTaxDocument.value.id
                    ? 'update'
                    : 'create',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
                submitButtonDisabled: disableCreate,
            }
        )
    }

    async function savePlatformTaxDocument(
        platformTaxDocument: PlatformTaxDocument,
        file: File
    ) {
        const taxDocumentsTemp = [...taxDocuments.value]
        if (file) {
            const uploadPrivateResponse = await uploadPrivate(file)
            platformTaxDocument.upload_key = uploadPrivateResponse.data.key
            platformTaxDocument.url =
                uploadPrivateResponse.data.temp_storage_url
        }

        if (platformTaxDocument.id) {
            taxDocuments.value[
                taxDocuments.value.findIndex(
                    (taxDocument: PlatformTaxDocument) =>
                        platformTaxDocument.id == taxDocument.id
                )
            ] = platformTaxDocument
        } else {
            taxDocuments.value.push(platformTaxDocument)
        }

        let toast = 'toast_success_platform_tax_document_updated'
        if (!platformTaxDocument.id) {
            toast = 'toast_success_platform_tax_document_created'
        }
        update(
            i18n.t(toast, {
                taxDocument: platformTaxDocument.name,
            })
        )
            .catch((error) => {
                taxDocuments.value = taxDocumentsTemp
                throw error
            })
            .then(() => {
                isModalOpen.value = false
            })
    }

    function handleDeletePlatformTaxDocuments() {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_tax_documents',
                title: 'modal_title_delete_platform_tax_documents',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deletePlatformTaxDocuments(),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: selectedPlatformTaxDocuments.value,
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deletePlatformTaxDocuments() {
        const taxDocumentsTemp = [...taxDocuments.value]
        selectedPlatformTaxDocuments.value.forEach((platformTaxDocument) => {
            deletePlatformTaxDocument(platformTaxDocument, false)
        })

        update(
            i18n.t('toast_success_platform_tax_documents_deleted', {
                taxDocuments: selectedPlatformTaxDocuments.value
                    .map((platformTaxDocument) => platformTaxDocument.name)
                    .join(', '),
            })
        )
            .catch((error) => {
                taxDocuments.value = taxDocumentsTemp
                throw error
            })
            .then(() => {
                selectedPlatformTaxDocumentsIds.value = []
                isModalOpen.value = false
            })
    }

    const selectedPlatformTaxDocuments = computed(() => {
        return taxDocuments.value.filter(
            (item: PlatformTaxDocument) =>
                item.id &&
                selectedPlatformTaxDocumentsIds.value.includes(item.id)
        )
    })

    return {
        PlatformTaxDocumentsTable,
        handleEditOrCreatePlatformTaxDocument,
        handleDeletePlatformTaxDocuments,
        selectedPlatformTaxDocumentsIds,
    }
}
